import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureAccess } from '../context/FeatureAccessContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    AreaChart, Area, Cell
} from 'recharts';
import {
    BookOpen, PenTool, GraduationCap, Brain, AlertTriangle, SpellCheck,
    Lock, X, Sparkles, Crown, Flame, Target, Trophy,
    Zap, ChevronRight, BarChart3, Star, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const tb = { fontFamily: "'Tiro Bangla', serif" };

// ===== Animated Counter =====
const AnimCounter = ({ end, duration = 1200 }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        let start = 0;
        const step = Math.max(1, Math.ceil(end / (duration / 16)));
        const timer = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(start);
        }, 16);
        return () => clearInterval(timer);
    }, [end, duration]);
    return <span>{count}</span>;
};

// ===== Locked Feature Modal =====
const LockedModal = ({ topic, onClose }) => {
    const [price, setPrice] = React.useState(135);
    const toBnNum = (n) => String(n).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]);
    React.useEffect(() => {
        if (topic) {
            axios.get(`${API}/pricing-config`).then(r => {
                const p = r.data.premium_weekly_price || r.data?.config?.premium_weekly_price;
                if (p) setPrice(p);
            }).catch(() => {});
        }
    }, [topic]);
    if (!topic) return null;
    const Icon = topic.icon;
    const messages = [
        "সাফল্যের পথে প্রতিটি পদক্ষেপ গুরুত্বপূর্ণ। এই section unlock করে নিজের সম্ভাবনা প্রকাশ করো!",
        "তুমি যে শুরু করেছো সেটাই সবচেয়ে বড় সাহস! আরো এগিয়ে যেতে এই feature unlock করো।",
    ];
    const msg = messages[Math.floor(Math.random() * messages.length)];
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <div className={`h-1.5 w-full bg-gradient-to-r ${topic.gradient}`} />
                <div className="p-6 relative">
                    <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                        <X className="w-3.5 h-3.5 text-gray-500" />
                    </button>
                    <div className="flex justify-center mb-4">
                        <div className="relative">
                            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${topic.gradient} flex items-center justify-center shadow-lg`}>
                                <Icon className="w-8 h-8 text-white" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
                                <Lock className="w-3 h-3 text-white" />
                            </div>
                        </div>
                    </div>
                    <h3 className="text-center text-lg font-bold text-gray-800 mb-1" style={tb}>{topic.titleBn}</h3>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 my-4 border border-purple-100">
                        <p className="text-sm text-gray-700 leading-relaxed" style={tb}><Sparkles className="w-4 h-4 inline mr-1 text-purple-500" />{msg}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 mb-4 text-center">
                        <Crown className="w-4 h-4 text-purple-600 inline mr-1" />
                        <span className="font-bold text-purple-700" style={tb}>Premium — মাত্র {toBnNum(price)} টাকা/সপ্তাহ</span>
                    </div>
                    <a href="mailto:info@bakkoshilpi.com?subject=Premium Unlock"
                        className="w-full py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold shadow-lg shadow-purple-500/25 hover:opacity-90 transition-all block text-center" style={tb}>
                        <Sparkles className="w-4 h-4 inline mr-1.5" />এখনই Unlock করো
                    </a>
                </div>
            </div>
        </div>
    );
};

// ===== Filter Presets =====
const FILTER_PRESETS = [
    { key: '7d', label: '৭ দিন', days: 7 },
    { key: '30d', label: '৩০ দিন', days: 30 },
    { key: 'custom', label: 'তারিখ', days: 0 },
];

// ===== Quick Action Cards =====
const QUICK_ACTIONS = [
    { key: 'sections', titleBn: '৩০টি গুরুত্বপূর্ণ Grammar', titleEn: '30 Important Grammar Sections', icon: BookOpen, path: '/learning-hub', gradient: 'from-teal-400 to-cyan-500', iconBg: 'bg-teal-100', iconClr: 'text-teal-600' },
    { key: 'builder', titleBn: 'বাক্য নির্মাণ', titleEn: 'Sentence Builder', icon: PenTool, path: '/sentence-builder', gradient: 'from-indigo-400 to-blue-500', iconBg: 'bg-indigo-100', iconClr: 'text-indigo-600' },
    { key: 'lesson', titleBn: 'আজকের শেখা', titleEn: "Today's Lesson", icon: GraduationCap, path: '/lesson', gradient: 'from-purple-400 to-violet-500', iconBg: 'bg-purple-100', iconClr: 'text-purple-600' },
    { key: 'drills', titleBn: 'অনুশীলন', titleEn: 'Daily practice exercises', icon: Brain, path: '/drills', gradient: 'from-orange-400 to-amber-500', iconBg: 'bg-orange-100', iconClr: 'text-orange-600' },
    { key: 'spelling', titleBn: 'বানান', titleEn: 'Improve your spelling', icon: SpellCheck, path: '/spelling-builder', gradient: 'from-pink-400 to-rose-500', iconBg: 'bg-pink-100', iconClr: 'text-pink-600' },
    { key: 'mistakes', titleBn: 'সাধারণ যে ভুল আমরা করে থাকি', titleEn: 'Common sentence mistakes', icon: AlertTriangle, path: '/common-mistakes', gradient: 'from-red-400 to-rose-500', iconBg: 'bg-red-100', iconClr: 'text-red-500' },
    { key: 'extra-grammar', titleBn: 'Extra Grammar', titleEn: 'Stage Learning', icon: BookOpen, path: '/stage-learning', gradient: 'from-amber-400 to-orange-500', iconBg: 'bg-amber-100', iconClr: 'text-amber-600' },
];

const DAYS_BN = ['সোম', 'মঙ্গল', 'বুধ', 'বৃহ', 'শুক্র', 'শনি', 'রবি'];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } } };

const StudentDashboard = () => {
    const { user } = useAuth();
    const { isLocked } = useFeatureAccess();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lockedModal, setLockedModal] = useState(null);
    const [filterKey, setFilterKey] = useState('7d');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);

    const fetchData = useCallback(async (days = 7, startDate, endDate) => {
        try {
            let url = `${API}/analytics/user/${user?.id}?days=${days}`;
            if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
            const res = await axios.get(url);
            setAnalytics(res.data);
            // Check for level up celebration
            if (res.data?.points?.just_leveled_up) {
                setShowCelebration(true);
            }
        } catch { /* ok */ } finally { setLoading(false); }
    }, [user?.id]);

    useEffect(() => { fetchData(7); }, [fetchData]);

    const applyFilter = (key) => {
        setFilterKey(key);
        if (key === '7d') { fetchData(7); setShowDatePicker(false); }
        else if (key === '30d') { fetchData(30); setShowDatePicker(false); }
        else setShowDatePicker(true);
    };

    const applyCustomDate = () => {
        if (customStart && customEnd) {
            fetchData(0, customStart, customEnd);
            setShowDatePicker(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-200 border-t-purple-600" />
                    <p className="text-sm text-slate-400" style={tb}>লোড হচ্ছে...</p>
                </div>
            </div>
        );
    }

    // ===== Compute Analytics =====
    const totalSubmissions = analytics?.total_submissions || 0;
    const accuracyTrend = analytics?.accuracy_trend || [];
    const dateLabels = analytics?.date_labels || [];
    const streak = analytics?.streak || user?.streak || 0;
    const topMistakes = analytics?.top_mistakes || [];
    const totalMistakes = topMistakes.reduce((s, m) => s + (m.count || 0), 0);
    const points = analytics?.points || { total: 0, level: 1, level_name: 'Starter', next_level_at: 500, points_to_next: 500 };
    const avgAccuracy = accuracyTrend.some(v => v > 0)
        ? Math.round(accuracyTrend.filter(v => v > 0).reduce((a,b) => a+b, 0) / accuracyTrend.filter(v => v > 0).length)
        : totalSubmissions > 0 ? 75 : 0;
    const levelName = points.level_name || 'Starter';
    const levelProgress = points.next_level_at > 0 ? Math.round(((points.next_level_at - points.points_to_next) / points.next_level_at) * 100) : 0;

    // Skill distribution from backend
    const skillData = analytics?.skill_distribution || [
        { name: 'Grammar', value: 0 }, { name: 'Sentence Making', value: 0 },
        { name: 'Vocabulary', value: 0 }, { name: 'Writing', value: 0 },
    ];
    const SKILL_STYLES = {
        'Grammar': { color: '#10B981', bg: 'bg-emerald-100', text: 'text-emerald-600', gradient: 'from-emerald-400 to-teal-500' },
        'Sentence Making': { color: '#8B5CF6', bg: 'bg-purple-100', text: 'text-purple-600', gradient: 'from-purple-400 to-indigo-500' },
        'Vocabulary': { color: '#F59E0B', bg: 'bg-amber-100', text: 'text-amber-600', gradient: 'from-amber-400 to-orange-500' },
        'Writing': { color: '#3B82F6', bg: 'bg-blue-100', text: 'text-blue-600', gradient: 'from-blue-400 to-indigo-500' },
    };

    // Chart data
    const chartLabels = dateLabels.length > 0
        ? dateLabels.map(d => { const dt = new Date(d); return DAYS_BN[dt.getDay() === 0 ? 6 : dt.getDay() - 1]; })
        : DAYS_BN;

    const weeklyActivity = chartLabels.map((day, i) => ({
        day, count: accuracyTrend[i] || 0,
    }));
    const accuracyData = chartLabels.map((day, i) => ({ day, accuracy: accuracyTrend[i] || 0 }));

    return (
        <div className="relative" data-testid="student-dashboard">
            {/* ===== BACKGROUND LAYERS ===== */}
            <div className="fixed inset-0 -z-10" style={{ background: 'linear-gradient(160deg, #EDE9FE 0%, #FCE7F3 25%, #E0E7FF 50%, #FDF2F8 75%, #F5F3FF 100%)' }} />
            {/* Subtle radial glow at bottom */}
            <div className="fixed bottom-0 left-0 right-0 h-[40vh] -z-10 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center bottom, rgba(196,181,253,0.25) 0%, transparent 70%)' }} />
            {/* Subtle sparkle dots */}
            <div className="fixed top-[20%] right-[15%] w-2 h-2 rounded-full bg-purple-300/40 -z-10 animate-pulse" />
            <div className="fixed top-[40%] left-[10%] w-1.5 h-1.5 rounded-full bg-pink-300/30 -z-10 animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="fixed bottom-[30%] right-[25%] w-2.5 h-2.5 rounded-full bg-indigo-300/20 -z-10 animate-pulse" style={{ animationDelay: '2s' }} />

            <LockedModal topic={lockedModal} onClose={() => setLockedModal(null)} />

            {/* ===== LEVEL UP CELEBRATION ===== */}
            {showCelebration && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowCelebration(false)}>
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                        className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="h-2 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400" />
                        <div className="p-8 text-center">
                            <motion.div
                                animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.2, 1] }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-200"
                            >
                                <Trophy className="w-10 h-10 text-white" />
                            </motion.div>
                            <motion.h2
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="text-2xl font-extrabold text-slate-800 mb-1" style={tb}
                            >
                                Congratulations!
                            </motion.h2>
                            <motion.p
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.55 }}
                                className="text-sm text-slate-500 mb-4" style={tb}
                            >
                                তুমি <span className="font-bold text-purple-600">{points.level_name}</span> এ উন্নীত হয়েছো!
                            </motion.p>
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.7, type: 'spring' }}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 mb-5"
                            >
                                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                                <span className="text-lg font-extrabold text-amber-600">{points.total} Points</span>
                            </motion.div>
                            <p className="text-xs text-slate-400 mb-5" style={tb}>
                                আরো {points.points_to_next} points হলে পরবর্তী level!
                            </p>
                            <button
                                onClick={() => setShowCelebration(false)}
                                className="w-full py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm shadow-lg hover:opacity-90 transition-all"
                                style={tb}
                            >
                                চালিয়ে যাও!
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
                <motion.div variants={container} initial="hidden" animate="show">

                    {/* ===== WELCOME BAR + POINTS ===== */}
                    <motion.div variants={item} className="mb-5" data-testid="dashboard-hero">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" style={tb}>
                                    Welcome Back, {user?.name?.split(' ')[0] || 'Student'}!
                                </h1>
                                <p className="text-sm text-slate-400 mt-0.5" style={tb}>Let's build 5 sentences today.</p>
                            </div>
                            {/* Points + Stats */}
                            <div className="bg-white/50 backdrop-blur-2xl rounded-2xl px-4 py-2.5 border border-white/70 shadow-lg shadow-purple-100/30" data-testid="points-card">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                                            <Star className="w-4 h-4 text-white fill-white" />
                                        </div>
                                        <div>
                                            <p className="text-lg font-extrabold text-amber-600 leading-none"><AnimCounter end={points.total} /></p>
                                            <p className="text-[9px] text-slate-400 leading-none mt-0.5">Points</p>
                                        </div>
                                    </div>
                                    <div className="w-px h-7 bg-slate-200/80" />
                                    <div className="flex items-center gap-1">
                                        <Flame className="w-3.5 h-3.5 text-orange-500" />
                                        <span className="text-sm font-bold text-orange-600">{streak}</span>
                                    </div>
                                    <div className="w-px h-7 bg-slate-200/80 hidden sm:block" />
                                    <div className="items-center gap-1 hidden sm:flex">
                                        <Zap className="w-3.5 h-3.5 text-emerald-500" />
                                        <span className="text-xs font-bold text-emerald-600">{levelName}</span>
                                    </div>
                                    <div className="w-px h-7 bg-slate-200/80 hidden sm:block" />
                                    <div className="items-center gap-1 hidden sm:flex">
                                        <Target className="w-3.5 h-3.5 text-indigo-500" />
                                        <span className="text-xs font-bold text-indigo-600">{avgAccuracy}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* ===== QUICK ACTION CARDS ===== */}
                    <motion.div variants={item} className="mb-7" data-testid="quick-actions">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-3.5">
                            {QUICK_ACTIONS.map((action) => {
                                const locked = isLocked(action.key);
                                const Icon = action.icon;
                                if (locked) {
                                    return (
                                        <motion.button key={action.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                                            onClick={() => setLockedModal(action)}
                                            className="relative group rounded-2xl p-4 text-left bg-white/30 backdrop-blur-xl border border-white/40 shadow-sm"
                                            data-testid={`topic-${action.key}`}>
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                                                <Lock className="w-2.5 h-2.5 text-amber-500" />
                                            </div>
                                            <div className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center mb-2.5">
                                                <Icon className="w-5 h-5 text-slate-400" />
                                            </div>
                                            <p className="font-bold text-slate-400 text-sm" style={tb}>{action.titleBn}</p>
                                            <p className="text-[11px] text-slate-300 mt-0.5">{action.titleEn}</p>
                                        </motion.button>
                                    );
                                }
                                return (
                                    <motion.div key={action.key} whileHover={{ y: -4, scale: 1.01 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                                        <Link to={action.path}
                                            className="block group rounded-2xl p-4 bg-white/45 backdrop-blur-xl border border-white/50 shadow-md shadow-purple-50/30 transition-all duration-300 hover:shadow-xl hover:shadow-purple-100/50 hover:bg-white/65"
                                            data-testid={`topic-${action.key}`}>
                                            <div className={`w-11 h-11 rounded-2xl ${action.iconBg} flex items-center justify-center mb-2.5`}>
                                                <Icon className={`w-5 h-5 ${action.iconClr}`} />
                                            </div>
                                            <p className="font-bold text-slate-800 text-sm" style={tb}>{action.titleBn}</p>
                                            <div className="flex items-center justify-between mt-0.5">
                                                <p className="text-[11px] text-slate-400">{action.titleEn}</p>
                                                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all" />
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>

                    {/* ===== GROWTH DASHBOARD ===== */}
                    <motion.div variants={item} className="mb-5" data-testid="growth-dashboard">
                        <div className="bg-white/35 backdrop-blur-2xl rounded-3xl p-4 sm:p-5 border border-white/50 shadow-lg shadow-purple-50/30">
                            {/* Header + Filter */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-purple-500" />
                                    <h2 className="text-lg font-bold text-slate-800" style={tb}>Your Growth Dashboard</h2>
                                </div>
                                {/* Date Filter */}
                                <div className="flex items-center gap-2 flex-wrap" data-testid="growth-filter">
                                    {FILTER_PRESETS.map(f => (
                                        <button key={f.key} onClick={() => applyFilter(f.key)}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                                filterKey === f.key
                                                    ? 'bg-purple-600 text-white shadow-sm'
                                                    : 'bg-white/60 text-slate-500 hover:bg-purple-50 border border-slate-200/60'
                                            }`}
                                            data-testid={`filter-${f.key}`} style={tb}>
                                            {f.key === 'custom' && <Calendar className="w-3 h-3" />}
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Custom Date Picker */}
                            {showDatePicker && (
                                <div className="flex items-center gap-2 mb-4 flex-wrap bg-white/50 rounded-xl p-3 border border-purple-100" data-testid="date-picker">
                                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                        className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none" />
                                    <span className="text-slate-400 text-xs">→</span>
                                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                        className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none" />
                                    <button onClick={applyCustomDate}
                                        className="px-4 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 transition-colors" style={tb}>
                                        ফিল্টার
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                                {/* Weekly Activity */}
                                <div className="bg-white/55 backdrop-blur-xl rounded-2xl p-4 border border-white/40" data-testid="weekly-activity-chart">
                                    <h3 className="text-xs font-bold text-slate-600 mb-2.5">Weekly Activity</h3>
                                    <ResponsiveContainer width="100%" height={130}>
                                        <BarChart data={weeklyActivity} barCategoryGap="18%">
                                            <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                                            <YAxis hide />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', fontSize: 11 }} cursor={{ fill: 'rgba(139,92,246,0.04)' }} />
                                            <Bar dataKey="count" radius={[6,6,0,0]} name="Activity">
                                                {weeklyActivity.map((entry, idx) => (
                                                    <Cell key={idx} fill={entry.count > 70 ? '#8B5CF6' : entry.count > 0 ? '#34D399' : '#E2E8F0'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                    <p className="text-[9px] text-slate-400 mt-1" style={tb}>Consistency builds fluency</p>
                                </div>

                                {/* Accuracy */}
                                <div className="bg-white/55 backdrop-blur-xl rounded-2xl p-4 border border-white/40" data-testid="accuracy-chart">
                                    <div className="flex items-center justify-between mb-2.5">
                                        <h3 className="text-xs font-bold text-slate-600">Accuracy</h3>
                                        <span className="text-lg font-extrabold text-purple-600">{avgAccuracy}%</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={130}>
                                        <AreaChart data={accuracyData}>
                                            <defs>
                                                <linearGradient id="accGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.25} />
                                                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                                            <YAxis hide domain={[0, 100]} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', fontSize: 11 }} />
                                            <Area type="monotone" dataKey="accuracy" stroke="#8B5CF6" strokeWidth={2.5} fill="url(#accGrad)" dot={{ r: 3, fill: '#8B5CF6', stroke: '#fff', strokeWidth: 2 }} name="Accuracy %" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Skill Distribution */}
                                <div className="bg-white/55 backdrop-blur-xl rounded-2xl p-4 border border-white/40" data-testid="skill-chart">
                                    <h3 className="text-xs font-bold text-slate-600 mb-3">Skill Distribution</h3>
                                    <div className="space-y-3">
                                        {skillData.map((skill, idx) => {
                                            const style = SKILL_STYLES[skill.name] || { color: '#6366f1', bg: 'bg-slate-100', text: 'text-slate-600', gradient: 'from-slate-400 to-slate-500' };
                                            return (
                                                <div key={idx} data-testid={`skill-${idx}`}>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[11px] font-semibold text-slate-600">{skill.name}</span>
                                                        <span className={`text-[11px] font-bold ${style.text}`}>{skill.value}%</span>
                                                    </div>
                                                    <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                                                        <motion.div
                                                            initial={{ width: 0 }}
                                                            animate={{ width: `${Math.min(skill.value, 100)}%` }}
                                                            transition={{ duration: 1, delay: idx * 0.15, ease: 'easeOut' }}
                                                            className={`h-full rounded-full bg-gradient-to-r ${style.gradient}`}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[9px] text-slate-400 mt-2.5" style={tb}>Practice more to grow each skill</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* ===== PERFORMANCE STATS ===== */}
                    <motion.div variants={item} data-testid="performance-stats">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                { label: 'Sentences Built', value: totalSubmissions, icon: PenTool, color: 'text-indigo-600', bg: 'from-indigo-50/80 to-blue-50/80', iconBg: 'bg-indigo-100', iconColor: 'text-indigo-500' },
                                { label: 'Mistakes Fixed', value: totalMistakes, icon: AlertTriangle, color: 'text-rose-500', bg: 'from-rose-50/80 to-pink-50/80', iconBg: 'bg-rose-100', iconColor: 'text-rose-500' },
                                { label: 'Level', value: levelName, icon: Trophy, color: 'text-purple-600', bg: 'from-purple-50/80 to-violet-50/80', iconBg: 'bg-purple-100', iconColor: 'text-purple-500', isText: true },
                                { label: 'Longest Streak', value: streak, icon: Flame, color: 'text-emerald-600', bg: 'from-emerald-50/80 to-teal-50/80', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-500' },
                            ].map((stat, i) => {
                                const SIcon = stat.icon;
                                return (
                                    <motion.div key={i} whileHover={{ y: -3 }}
                                        className={`bg-gradient-to-br ${stat.bg} backdrop-blur-xl rounded-2xl p-3.5 border border-white/50 shadow-sm text-center`}
                                        data-testid={`stat-${i}`}>
                                        <div className={`w-8 h-8 rounded-xl ${stat.iconBg} flex items-center justify-center mx-auto mb-1.5`}>
                                            <SIcon className={`w-3.5 h-3.5 ${stat.iconColor}`} />
                                        </div>
                                        <p className={`text-xl font-extrabold ${stat.color}`}>
                                            {stat.isText ? stat.value : <AnimCounter end={stat.value} />}
                                        </p>
                                        <p className="text-[10px] text-slate-400 mt-0.5">{stat.label}</p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>

                </motion.div>
            </div>
        </div>
    );
};

export default StudentDashboard;
